import React from 'react';
import type { Language, NutritionFactsPer100g } from '../types';
import ChevronDownIcon from './icons/ChevronDownIcon';
import { getTranslation } from '../utils/translations';

interface NutritionFactsPanelProps {
    facts?: NutritionFactsPer100g;
    language?: Language;
    collapsed?: boolean;
    onToggleCollapse?: () => void;
    isLoading?: boolean;
    showRecalculate?: boolean;
    isRecalculating?: boolean;
    onRecalculate?: () => void;
}

const NutritionFactsPanel: React.FC<NutritionFactsPanelProps> = ({
    facts,
    language,
    collapsed = false,
    onToggleCollapse,
    isLoading = false,
    showRecalculate = false,
    isRecalculating = false,
    onRecalculate,
}) => {
    const t = (key: string) => getTranslation(language, key);

    const caloriesFromFatPercent = (f: NutritionFactsPer100g): number => {
        if (f.calories > 0) {
            return Math.round((f.fatGrams * 9 / f.calories) * 1000) / 10;
        }
        return f.fatPercentage;
    };

    const Row = ({ label, value }: { label: string; value: string }) => (
        <div className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</span>
            <span className="text-sm font-mono font-bold text-slate-800 dark:text-slate-100">{value}</span>
        </div>
    );

    return (
        <div className="rounded-2xl border border-stone-200 dark:border-slate-700 overflow-hidden bg-slate-50/50 dark:bg-slate-900/40">
            <div
                className={`flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-900 ${onToggleCollapse ? 'cursor-pointer' : ''}`}
                onClick={onToggleCollapse}
            >
                <div className="flex items-center gap-2">
                    {onToggleCollapse && (
                        <div className={`text-slate-400 transition-transform ${collapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`}>
                            <ChevronDownIcon className="w-4 h-4" />
                        </div>
                    )}
                    <div>
                        <h3 className="text-sm font-black text-slate-700 dark:text-slate-200 uppercase tracking-widest">{t('nutritionFacts')}</h3>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{t('nutritionPer100g')}</p>
                    </div>
                </div>
                {showRecalculate && onRecalculate && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRecalculate();
                        }}
                        disabled={isRecalculating}
                        className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest bg-brand-orange/10 text-brand-orange border border-brand-orange/20 hover:bg-brand-orange/20 disabled:opacity-50"
                    >
                        {isRecalculating ? t('nutritionCalculating') : t('recalculateNutrition')}
                    </button>
                )}
            </div>

            {!collapsed && (
                <div className="px-4 py-3">
                    {isLoading || isRecalculating ? (
                        <div className="flex items-center justify-center py-6 text-slate-400 text-sm font-medium">
                            <div className="w-5 h-5 border-2 border-brand-orange/30 border-t-brand-orange rounded-full animate-spin mr-2" />
                            {t('nutritionCalculating')}
                        </div>
                    ) : facts ? (
                        <div>
                            <Row label={t('calories')} value={`${facts.calories}`} />
                            <Row label={t('fat')} value={`${facts.fatGrams}g (${facts.fatGrams}%)`} />
                            <Row label={t('caloriesFromFat')} value={`${caloriesFromFatPercent(facts)}%`} />
                            <Row label={t('sodium')} value={`${facts.sodiumMg}mg`} />
                            <Row label={t('dietaryFiber')} value={`${facts.dietaryFiberGrams}g`} />
                            <Row label={t('totalSugars')} value={`${facts.totalSugarsGrams}g`} />
                            <Row label={t('addedSugars')} value={`${facts.addedSugarsGrams}g`} />
                            <Row label={t('protein')} value={`${facts.proteinGrams}g`} />
                        </div>
                    ) : (
                        <p className="text-sm text-slate-400 text-center py-4">{t('nutritionUnavailable')}</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default NutritionFactsPanel;
